/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The deal behind an OLX ad. OLX takes one price per ad, so a property offered
 * for sale AND rent publishes a single ad: the broker chooses which deal is the
 * main one (it sets the ad's type and price) and the other price goes into the
 * description. The backend lists the deals the property offers with a price
 * (`GET .../olx/phones` → `transactions`) and keeps the choice for edits and
 * re-sends.
 */
export type OlxDealType = 'sale' | 'rental';

/** A deal the property offers with a usable price. */
export interface OlxDeal {
  type: OlxDealType;
  /** Whole reais (rent is per month). */
  price: number;
}

const DEAL_TYPES: ReadonlySet<string> = new Set<OlxDealType>(['sale', 'rental']);

/**
 * The deals of an options response, dropping anything that is not a known deal
 * with a positive price.
 *
 * @param raw - The `transactions` field of the OLX options response.
 * @returns The usable deals, in the order the backend listed them (sale first).
 */
export function parseOlxDeals(raw: unknown): OlxDeal[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (deal): deal is OlxDeal =>
      DEAL_TYPES.has(deal?.type) && typeof deal?.price === 'number' && deal.price > 0,
  );
}

/** Whether the broker has to say which deal is the ad's main one. */
export function mustChooseOlxDeal(deals: OlxDeal[]): boolean {
  return deals.length > 1;
}

/**
 * The deal preselected when the dialog opens: the one chosen at the last
 * publish if the property still offers it, else none -- the broker has to pick.
 */
export function initialOlxDealChoice(deals: OlxDeal[], stored: unknown): OlxDealType | null {
  return deals.find((deal) => deal.type === stored)?.type ?? null;
}

/**
 * The `olx_transaction` to publish with: the broker's choice when there is one
 * to make, null otherwise (a single deal needs no choice; the backend uses it).
 */
export function resolveOlxTransaction(deals: OlxDeal[], choice: OlxDealType | null): OlxDealType | null {
  return mustChooseOlxDeal(deals) && deals.some((deal) => deal.type === choice) ? choice : null;
}

/** Whether publishing must wait for the broker to choose the main deal. */
export function olxDealMissing(deals: OlxDeal[], choice: OlxDealType | null): boolean {
  return mustChooseOlxDeal(deals) && resolveOlxTransaction(deals, choice) === null;
}

/** A price as the broker reads it: `R$ 2.827.019`. */
export function formatOlxDealPrice(price: number): string {
  return price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}
